import React, { useState, useEffect, ReactNode } from 'react';
import { useI18n } from '../../../hooks/useI18n';
import { useNotifications } from '../../../contexts/NotificationContext';
import Modal from '../../../components/ui/Modal';
import { Input } from '../../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select-shadcn';
import { Toggle } from '../../../components/ui/Toggle';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import { aclApi } from '../api/aclApi';
import type { AclNode } from '../types';
import { useAclConfigStore } from '../../../store/aclConfig';

type FormFieldProps = {
  id: string;
  label: string;
  tooltip: string;
  children: ReactNode;
  required?: boolean;
};

const FormField = ({ id, label, tooltip, children, required }: FormFieldProps) => (
  <div className="flex items-center gap-4">
    <SimpleTooltip content={tooltip}>
      <Label
        htmlFor={id}
        className="text-sm font-medium text-foreground w-36 flex-shrink-0 h-10 flex items-center"
      >
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </Label>
    </SimpleTooltip>
    <div className="flex-1">
      {children}
    </div>
  </div>
);

interface AclRuleModalProps {
  isOpen: boolean;
  onClose: () => void;
  rule: AclNode | null;
  onSuccess: () => void;
}

export function AclRuleModal({ isOpen, onClose, rule, onSuccess }: AclRuleModalProps) {
  const { t } = useI18n();
  const { showSuccess, showError } = useNotifications();
  const { markPendingChanges } = useAclConfigStore();
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    listName: 'sbc_trust',
    defaultAction: 'deny' as 'allow' | 'deny',
    nodeType: 'allow' as 'allow' | 'deny',
    cidr: '',
    description: '',
    enabled: true,
  });

  useEffect(() => {
    if (isOpen && rule) {
      setFormData({
        listName: rule.listName,
        defaultAction: rule.defaultAction,
        nodeType: rule.nodeType,
        cidr: rule.cidr,
        description: rule.description || '',
        enabled: rule.enabled,
      });
    } else if (isOpen) {
      setFormData({
        listName: 'sbc_trust',
        defaultAction: 'deny',
        nodeType: 'allow',
        cidr: '',
        description: '',
        enabled: true,
      });
    }
  }, [isOpen, rule]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.cidr || !formData.listName) {
      showError('List Name and CIDR are required');
      return;
    }

    setLoading(true);
    try {
      if (rule) {
        await aclApi.update(rule.id, formData);
        showSuccess(t('acl.notifications.updateSuccess', 'ACL Rule updated successfully'));
      } else {
        await aclApi.create(formData);
        showSuccess(t('acl.notifications.createSuccess', 'ACL Rule created successfully'));
      }
      markPendingChanges();
      onSuccess();
    } catch (error: any) {
      showError(error.message || 'Failed to save ACL Rule');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={rule ? t('acl.modal.editTitle', 'Edit ACL Rule') : t('acl.modal.addTitle', 'Add ACL Rule')}
      size="md"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-4">
          <FormField
            id="listName"
            label={t('acl.fields.listName', 'List Name')}
            tooltip={t('acl.tooltips.listName', 'The FreeSWITCH ACL list name (e.g., sbc_trust)')}
            required
          >
            <Input
              id="listName"
              value={formData.listName}
              onChange={(e) => setFormData({ ...formData, listName: e.target.value })}
              placeholder="sbc_trust"
              disabled={loading}
              className="font-mono text-sm"
            />
          </FormField>

          <FormField
            id="cidr"
            label={t('acl.fields.cidr', 'CIDR')}
            tooltip={t('acl.tooltips.cidr', 'IP Address and Mask (e.g., 192.168.1.0/24 or 10.9.0.2/32)')}
            required
          >
            <Input
              id="cidr"
              value={formData.cidr}
              onChange={(e) => setFormData({ ...formData, cidr: e.target.value })}
              placeholder="192.168.1.0/24"
              disabled={loading}
              className="font-mono text-sm"
            />
          </FormField>

          <FormField
            id="nodeType"
            label={t('acl.fields.nodeType', 'Rule Type')}
            tooltip={t('acl.tooltips.nodeType', 'Whether to allow or deny this CIDR')}
            required
          >
            <Select
              value={formData.nodeType}
              onValueChange={(value: 'allow' | 'deny') => setFormData({ ...formData, nodeType: value })}
            >
              <SelectTrigger id="nodeType" disabled={loading}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="allow">{t('acl.options.allow', 'Allow')}</SelectItem>
                <SelectItem value="deny">{t('acl.options.deny', 'Deny')}</SelectItem>
              </SelectContent>
            </Select>
          </FormField>

          <FormField
            id="description"
            label={t('common.description', 'Description')}
            tooltip={t('acl.tooltips.description', 'Optional description for this rule')}
          >
            <Input
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              disabled={loading}
            />
          </FormField>

          <FormField
            id="enabled"
            label={t('common.enabled', 'Enabled')}
            tooltip={t('acl.tooltips.enabled', 'Toggle this rule on or off')}
          >
            <Toggle
              value={formData.enabled}
              onChange={(value) => setFormData({ ...formData, enabled: value })}
              disabled={loading}
            />
          </FormField>
        </div>

        <div className="flex justify-end gap-3 pt-6 border-t border-border">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={loading}
          >
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            type="submit"
            disabled={loading || !formData.listName || !formData.cidr}
          >
            {loading ? t('common.saving', 'Saving...') : (rule ? t('common.save', 'Save') : t('common.add', 'Add'))}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
